export type PseudolocMode = 'off' | 'normal' | 'wide' | 'key';

// U+27E6 / U+27E7 — intentionally NOT [ ] which are used by wrapper syntax in interpolation
export const PSEUDOLOC_START_SENTINEL = '⟦';
export const PSEUDOLOC_END_SENTINEL = '⟧';
// Edge-case characters for rendering/encoding stress testing:
// 𝓐 (U+1D4D0) — astral plane (surrogate pair in UTF-16)
// é (e + U+0301) — combining accent (composed character)
// ẇ (U+1E87) — dot above, tests vertical ascender overflow
// ḍ (U+1E0D) — dot below, tests descender overflow
// ƒ (U+0192) — hook descender extends well below baseline
export const PSEUDOLOC_EDGE_CHARS = '𝓐e\u0301ẇḍƒ';
export const PSEUDOLOC_PADDING_CHAR = '~';
export const PSEUDOLOC_MAX_RUN_LENGTH = 15;

export const PSEUDOLOC_NORMAL_PADDING_RATIO = 0.3;
export const PSEUDOLOC_NORMAL_MIN_PADDING_LENGTH = 3;

export const PSEUDOLOC_WIDE_PADDING_RATIO = 0.8;
export const PSEUDOLOC_WIDE_MIN_PADDING_LENGTH = 10;

const buildPadding = (length: number): string => {
  if (length <= PSEUDOLOC_MAX_RUN_LENGTH) {
    return PSEUDOLOC_PADDING_CHAR.repeat(length);
  }

  const chunks: string[] = [];
  let remaining = length;

  // First chunk is always the max run length
  chunks.push(PSEUDOLOC_PADDING_CHAR.repeat(PSEUDOLOC_MAX_RUN_LENGTH));
  remaining -= PSEUDOLOC_MAX_RUN_LENGTH;

  while (remaining > 0) {
    const chunkSize = Math.min(remaining, PSEUDOLOC_MAX_RUN_LENGTH);
    chunks.push(PSEUDOLOC_PADDING_CHAR.repeat(chunkSize));
    remaining -= chunkSize;
  }

  return chunks.join(' ');
};

export const applyPseudoloc = (value: string, mode: 'normal' | 'wide' = 'normal'): string => {
  const ratio = mode === 'wide' ? PSEUDOLOC_WIDE_PADDING_RATIO : PSEUDOLOC_NORMAL_PADDING_RATIO;
  const minLength = mode === 'wide' ? PSEUDOLOC_WIDE_MIN_PADDING_LENGTH : PSEUDOLOC_NORMAL_MIN_PADDING_LENGTH;
  const paddingLength = Math.max(minLength, Math.ceil(value.length * ratio));
  const padding = buildPadding(paddingLength);

  return `${PSEUDOLOC_START_SENTINEL}${value} ${padding}${PSEUDOLOC_EDGE_CHARS}${PSEUDOLOC_END_SENTINEL}`;
};
